import { AuthService } from '@/app/core/services/auth.service';
import type { Usuario } from '@/app/models';
import { DialogComponent } from '../dialog/dialog.component';
import { environment } from '@/environments/environment';
import {
  Component,
  HostListener,
  Input,
  inject,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, ActivationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs';

export interface NavbarMenuItem {
  routerLink: string;
  titulo: string;
  iconoInactivo: string;
  iconoActivo: string;
  isForRoot: boolean;
}

@Component({
  selector: 'c-navbar',
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css',
  standalone: false,
})
export class NavbarComponent {
  /**
   * @description Si la feature no tiene sidebar propio (hoy solo
   * `home-page`, el selector de sistema): se oculta el ícono de tickets y el
   * botón de colapsar (no hay qué colapsar), y se muestra el logout
   * directo en la píldora de escritorio (en los demás casos, "Cerrar
   * sesión" vive en el sidebar). Ver plan/plan-20.
   */
  @Input() hasSidebar = true;
  @Input() menu: NavbarMenuItem[] = [];
  @Input() userPanelRoute = '/panel-usuario';

  readonly isTestEnvironment = !environment.production;

  //? Datos de usuario
  usuario!: Usuario | null;
  imgProfile!: string;

  //? Navegación
  breadcrumb: string[] = [];
  titulo = '';
  subtitulo = '';
  collapse = false;

  //? Desplegable de perfil (plan-22)
  profileMenuOpen = false;

  //? Inyecciones
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);

  /**
   * @description Para el desplegable de perfil: "Selección de sistema" se
   * oculta solo cuando ya estás parado en el propio selector (`/home`, sin
   * sidebar) — no tendría sentido linkear ahí mismo. En otras pantallas sin
   * sidebar (ej. Panel de Usuario, reachable desde `/home`) sí se muestra.
   */
  protected isHomeSelector(): boolean {
    return this.router.url.split('?')[0] === '/home';
  }

  async ngOnInit() {
    this.usuario = await this.auth.returnUserInfo();

    this.imgProfile = this.usuario?.avatar
      ? environment.images + this.usuario?.avatar
      : 'svg/profile.svg';

    // Obtener la data inicial de la ruta activa hasta la raíz
    this.updateRouteData();

    // Suscripción para actualizar datos y breadcrum en cada navegación
    this.router.events
      .pipe(
        filter((evt) => evt instanceof ActivationEnd),
        map((evt) => (evt as ActivationEnd).snapshot),
        // Sólo la ruta más profunda (sin hijos)
        filter((snap) => !snap.firstChild),
      )
      .subscribe(() => {
        // Recalcula recorriendo toda la jerarquía de rutas (root -> hoja),
        // heredando título/subtítulo/breadcrumb de los niveles padres cuando
        // la ruta más específica no define los suyos propios.
        //
        // Antes, acá se volvía a leer `snap.data` (solo de la ruta hoja) y
        // se pisaba el título/subtítulo ya calculados con `?? ''` — si la
        // hoja no tenía su propio `titulo` (ej. Dashboard, que hereda el de
        // su ruta padre "capacitaciones"), quedaba vacío. Encontrado y
        // corregido en plan-27 (ver plan/bugs.md).
        this.updateRouteData();
      });
  }

  /**
   * @description Actuliza los datos de navegación -> hacemos uso de
   * breadcrumb para identificar camino de rutas activas
   */
  private updateRouteData() {
    const dataPath = this.collectRouteData(this.route.root);

    dataPath.forEach((data) => {
      const { alias, titulo, subtitulo } = data;
      this.updateBreadcrumb(alias);
      this.titulo = titulo || this.titulo;
      this.subtitulo = subtitulo || this.subtitulo;
    });
  }

  /**
   * @description Recorre la jerarquía de rutas para obtener los datos asociados
   */
  private collectRouteData(route: ActivatedRoute): any[] {
    let routesData: any[] = [];

    while (route) {
      if (route.snapshot.data) {
        routesData.push(route.snapshot.data);
      }
      // Avanza al hijo, incluso en módulos cargados de forma diferida
      route = route.firstChild!;
    }

    return routesData;
  }

  /**
   * @description Actualiza "breadcrumb" según el alias actual -> visual
   */
  private updateBreadcrumb(alias: string | undefined) {
    if (alias) {
      const i = this.breadcrumb.findIndex((b) => b === alias);
      if (i >= 0) {
        this.breadcrumb.splice(i + 1);
      } else {
        this.breadcrumb.push(alias);
      }
    }
  }

  /**
   * @description Cierre de menú colapsado al redimensionar la ventana
   */
  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.collapse = false;
  }

  /**
   * @description Abre/cierra el desplegable de perfil (avatar en la píldora
   * del navbar). Ver plan/plan-22.
   */
  toggleProfileMenu(event: Event) {
    event.stopPropagation();
    this.profileMenuOpen = !this.profileMenuOpen;
    if (this.profileMenuOpen) this.notifOpen = false;
  }

  /**
   * @description Abre/cierra el panel de notificaciones (antes se abría con
   * hover, ahora con clic, mismo criterio que el menú de perfil). Ver
   * plan/plan-27.
   */
  notifOpen = false;
  toggleNotifOpen(event: Event) {
    event.stopPropagation();
    this.notifOpen = !this.notifOpen;
    if (this.notifOpen) this.profileMenuOpen = false;
  }

  /**
   * @description Cierra el desplegable de perfil y el panel de
   * notificaciones al hacer click afuera de cualquiera de los dos
   */
  @HostListener('document:click')
  closeMenus() {
    this.profileMenuOpen = false;
    this.notifOpen = false;
  }

  /**
   * @description Cierre de sesión
   */
  @ViewChild('dialog') dialog!: DialogComponent;
  logout() {
    this.dialog.confirm(
      '¿Realmente desea cerrar su sesión?',
      'Cierre de sesión',
      () => {
        this.auth.logout().subscribe();
      },
    );
  }
}
